/**
 * Dashboard — "what needs me today", then context.
 *
 * The queue row comes first because it is the only part that is actionable, and every tile
 * deep-links to the list ALREADY FILTERED to what it counted. Totals sit below: knowing
 * there are 154 users has never once changed what an admin does next.
 *
 * Two things it got wrong before, both of which made it under-report its own console:
 *
 *   It read one endpoint. `/admin/stats` knows about five queues, and the modules have
 *   since grown several more — products awaiting approval, bookings whose slot passed with
 *   nobody closing them out. So the dashboard would say "2 bookings awaiting a vendor"
 *   while 45 sat stuck, and every one of those modules already publishes its own counts.
 *
 *   Its links went stale. Each list page has been rebuilt on segments, and the KYC tile
 *   still pointed at `?status=DOCUMENTS_SUBMITTED` — a parameter the vendors page no
 *   longer reads. The tile said 4 and the list it opened showed 135, which is worse than
 *   not linking at all.
 */
import { Link } from 'react-router-dom';
import {
  CalendarCheck2,
  ClipboardCheck,
  CreditCard,
  Flag,
  LifeBuoy,
  Store,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { DetailPanel, PanelEmpty, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { TimeSeries, inr as chart_inr } from '@/components/charts';
import { ErrorState } from '@/components/ui/error-state';
import { ago, humanize, inr } from '@/lib/format';
import { use_auth_store } from '@/lib/auth-store';
import { use_admin_stats } from '@/features/stats/hooks';
import { use_approval_counts } from '@/features/product-approval/hooks';
import { use_booking_counts } from '@/features/bookings/hooks';
import { use_review_counts } from '@/features/reviews/hooks';

export function DashboardPage() {
  const user = use_auth_store((s) => s.user);
  const { data, isLoading, error } = use_admin_stats();

  /*
   * The other modules' own counts, rather than a second copy of the same sums in the stats
   * endpoint. Each of these is already fetched and cached by the module it belongs to, so
   * arriving here from Product approvals costs nothing — and a dashboard that computed its
   * own version of "needs review" would be a number that could disagree with the screen it
   * links to.
   */
  const { data: approvals } = use_approval_counts();
  const { data: bookings } = use_booking_counts();
  const { data: reviews } = use_review_counts();

  const queues = build_queues({ stats: data, approvals, bookings, reviews });
  const outstanding = queues.reduce((n, q) => n + (q.count ?? 0), 0);
  const loading = isLoading && !data;

  return (
    <DashboardLayout
      title="Dashboard"
      subtitle={
        loading
          ? `Welcome back, ${user?.first_name ?? 'Admin'}`
          : outstanding === 0
            ? 'Nothing is waiting on you right now'
            : `${outstanding} ${outstanding === 1 ? 'thing needs' : 'things need'} someone today`
      }
      stats={<MetricStrip metrics={build_money_metrics(data, bookings)} />}
    >
      {error && (
        <ErrorState
          title="Could not load the dashboard"
          description="The queues below may be incomplete."
        />
      )}

      <DetailPanel
        title="Needs attention"
        icon={TriangleAlert}
        description="Every tile opens the list already filtered to exactly what it counted"
        body_class="p-0"
        test_id="queue-section"
      >
        {loading ? (
          <div className="p-3.5">
            <PanelSkeleton rows={3} />
          </div>
        ) : outstanding === 0 ? (
          <div className="p-3.5" data-testid="queue-empty">
            <PanelEmpty icon={ClipboardCheck}>
              Nothing is waiting on you right now.
            </PanelEmpty>
          </div>
        ) : (
          <ul className="grid divide-border sm:grid-cols-2 sm:divide-x lg:grid-cols-3 xl:grid-cols-6">
            {queues.map((q) => (
              <li key={q.key} className="border-b border-border last:border-b-0 xl:border-b-0">
                <Link
                  to={q.to}
                  data-testid={`queue-${q.key}`}
                  className="flex h-full flex-col gap-1 px-3.5 py-3 transition-colors hover:bg-muted/50"
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-micro font-bold uppercase tracking-wide text-muted-foreground">
                      {q.label}
                    </span>
                    <span
                      className={
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-control ' +
                        ((q.count ?? 0) > 0
                          ? q.tone === 'destructive'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground')
                      }
                    >
                      <q.icon className="h-3.5 w-3.5" />
                    </span>
                  </span>
                  <span
                    className={
                      'font-heading text-2xl font-bold tabular ' +
                      ((q.count ?? 0) === 0
                        ? 'text-muted-foreground/50'
                        : q.tone === 'destructive'
                          ? 'text-destructive'
                          : 'text-foreground')
                    }
                  >
                    {q.count ?? '—'}
                  </span>
                  <span className="text-micro text-muted-foreground">{q.hint}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </DetailPanel>

      <div className="grid items-start gap-gutter lg:grid-cols-[2fr_1fr]">
        <DetailPanel
          title="Net revenue, last 14 days"
          icon={Wallet}
          description="Captured payments minus refunds"
          actions={
            <Link
              to="/reports"
              className="text-micro font-semibold text-primary underline-offset-2 hover:underline"
            >
              Full reports →
            </Link>
          }
          test_id="revenue-section"
        >
          {loading || !data ? (
            <PanelSkeleton rows={4} />
          ) : (
            <TimeSeries
              label="Net revenue over the last 14 days"
              format={(n) => chart_inr(n)}
              points={data.revenue.last_14_days.map((d) => ({
                label: d.date.slice(5),
                value: Number(d.net_inr),
              }))}
            />
          )}
        </DetailPanel>

        <DetailPanel title="Latest KYC submissions" icon={Store} body_class="p-0" test_id="recent-kyc">
          <Feed
            loading={loading}
            empty="No vendors are waiting on a review."
            rows={(data?.recent.kyc_submissions ?? []).map((v) => ({
              key: v.vendor_profile_id,
              to: `/vendors/${v.vendor_profile_id}`,
              title: v.business_name ?? v.vendor_profile_id,
              sub: [v.city, v.module_types.map(humanize).join(', ')].filter(Boolean).join(' · '),
              when: v.submitted_at,
            }))}
          />
        </DetailPanel>
      </div>

      <div className="grid items-start gap-gutter lg:grid-cols-2">
        <DetailPanel
          title="Open support requests"
          icon={LifeBuoy}
          body_class="p-0"
          test_id="recent-support"
        >
          <Feed
            loading={loading}
            empty="No open support requests."
            rows={(data?.recent.support_requests ?? []).map((r) => ({
              key: r.id,
              to: `/support-requests/${r.id}`,
              title: humanize(r.kind),
              sub: `${humanize(r.module_type)} · ${humanize(r.status)}`,
              when: r.created_at,
            }))}
          />
        </DetailPanel>

        <DetailPanel
          title="Latest bookings"
          icon={CalendarCheck2}
          body_class="p-0"
          test_id="recent-bookings"
        >
          <Feed
            loading={loading}
            empty="No bookings yet."
            rows={(data?.recent.bookings ?? []).map((b) => ({
              key: b.id,
              to: `/bookings/${b.id}`,
              title: humanize(b.module_type),
              sub: humanize(b.status),
              when: b.created_at,
            }))}
          />
        </DetailPanel>
      </div>
    </DashboardLayout>
  );
}

type QueueTile = {
  key: string;
  label: string;
  hint: string;
  count: number | undefined;
  icon: React.ComponentType<{ className?: string }>;
  /** Pre-filtered, so the list shows exactly what the number counted. */
  to: string;
  tone?: 'destructive';
};

function build_queues({
  stats,
  approvals,
  bookings,
  reviews,
}: {
  stats: ReturnType<typeof use_admin_stats>['data'];
  approvals: ReturnType<typeof use_approval_counts>['data'];
  bookings: ReturnType<typeof use_booking_counts>['data'];
  reviews: ReturnType<typeof use_review_counts>['data'];
}): QueueTile[] {
  return [
    {
      key: 'vendors_awaiting_kyc',
      label: 'KYC to review',
      hint: 'Vendors waiting on you',
      count: stats?.queues.vendors_awaiting_kyc,
      icon: ClipboardCheck,
      // `segment=queue`, not `status=DOCUMENTS_SUBMITTED`: the vendors page was rebuilt on
      // segments and quietly ignores the old parameter, so that link opened all 135.
      to: '/vendors?segment=queue',
    },
    {
      key: 'support_open',
      label: 'Support requests',
      hint: 'Open or awaiting agreement',
      count: stats?.queues.support_open,
      icon: LifeBuoy,
      to: '/support-requests?segment=open',
    },
    {
      key: 'product_approvals',
      label: 'Product approvals',
      hint: 'New listings and edits',
      count: approvals?.needs_review,
      icon: ClipboardCheck,
      to: '/product-approvals?segment=queue',
    },
    {
      key: 'bookings_stuck',
      label: 'Bookings stuck',
      // The biggest queue in the console, and the dashboard never mentioned it: the slot
      // passed and nobody completed, cancelled or refunded it.
      hint: 'Slot passed, still open',
      count: bookings?.needs_attention,
      icon: CalendarCheck2,
      to: '/bookings?segment=attention',
      tone: 'destructive',
    },
    {
      key: 'reviews_reported',
      label: 'Reported reviews',
      // Reported AND still visible — a report already dealt with is not a job.
      hint: 'Flagged and still showing',
      count: reviews?.unactioned,
      icon: Flag,
      to: '/reviews?segment=reports',
    },
    {
      key: 'orders_pending_payment',
      label: 'Unpaid orders',
      hint: 'Checkout never completed',
      count: stats?.queues.orders_pending_payment,
      icon: CreditCard,
      to: '/commerce-orders?segment=unpaid',
    },
  ];
}

function build_money_metrics(
  stats: ReturnType<typeof use_admin_stats>['data'],
  bookings: ReturnType<typeof use_booking_counts>['data'],
): Metric[] {
  return [
    {
      key: 'revenue_today',
      label: 'Taken today',
      value: stats ? inr(stats.revenue.today_inr) : undefined,
      hint: 'Captured, minus refunds',
      icon: Wallet,
      loading: !stats,
    },
    {
      key: 'revenue_month',
      label: 'Taken this month',
      value: stats ? inr(stats.revenue.month_inr) : undefined,
      to: '/reports',
      icon: Wallet,
      loading: !stats,
    },
    {
      key: 'bookings_today',
      label: 'Booked for today',
      value: bookings?.today?.toLocaleString('en-IN'),
      hint: 'Slots in the next 24 hours',
      to: '/bookings?segment=today',
      icon: CalendarCheck2,
      loading: !bookings,
    },
    {
      key: 'in_progress',
      label: 'Happening now',
      value: bookings?.in_progress?.toLocaleString('en-IN'),
      hint: 'Vendors mid-job',
      to: '/bookings?segment=in_progress',
      icon: CalendarCheck2,
      loading: !bookings,
    },
    {
      key: 'refunded',
      label: 'Refunded',
      value: bookings ? inr(bookings.refunded_inr) : undefined,
      hint: bookings ? `of ${inr(bookings.captured_inr)} taken on bookings` : undefined,
      to: '/payments?segment=refunds',
      icon: CreditCard,
      loading: !bookings,
    },
  ];
}

/** A short list of recent records, each linking to its own page. */
function Feed({
  rows,
  empty,
  loading,
}: {
  rows: { key: string; to: string; title: string; sub: string; when: string }[];
  empty: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="p-3.5">
        <PanelSkeleton rows={3} />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-3.5">
        <PanelEmpty>{empty}</PanelEmpty>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {rows.map((r) => (
        <li key={r.key}>
          <Link
            to={r.to}
            className="flex items-center justify-between gap-3 px-3.5 py-2.5 transition-colors hover:bg-muted/50"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-data font-medium">{r.title}</span>
              <span className="truncate text-micro text-muted-foreground">{r.sub}</span>
            </span>
            <span className="shrink-0 whitespace-nowrap text-micro text-muted-foreground">
              {ago(r.when)}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
